<template>
  <div class="event-start-stop-modal" v-if="event">
    <HMModalHeader
      :title="$t('EVENTS.TITLE.EVENT_DETAILS')"
      @close="$emit('close')"
    />
    <div v-if="!event" class="text-center">
      <HMSpinner />
    </div>
    <div v-else class="content">
      <div class="d-flex flex-row justify-content-start">
        <EventTypeComponent class="c-charcoal" :eventType="event.event_type" />
        <EventCancelledComponent v-if="event.is_cancelled" class="ml-4" />
      </div>
      <EventNameComponent
        class="h2 mb-3"
        :eventName="event.event_name"
        :isCancelled="event.is_cancelled"
      />
      <EventDateTimeComponent
        class="mb-1"
        :startTime="shortTime(event.event_start_datetime)"
        :endTime="shortTime(event.event_end_datetime)"
        :date="shortDate(event.event_start_datetime)"
      />
      <EventDurationComponent
        class="mb-1"
        :durationHours="event.duration_hours"
        :durationMinutes="event.duration_minutes"
      />
      <EventLocationComponent
        v-if="event.location_address"
        class="mb-1"
        :locationAddress="event.location_address"
      />
      <EventRepeatsComponent
        v-if="event.event_schedule"
        class="mb-1"
        :dayOfWeek="event.schedule_day_of_week"
        :scheduleEndDate="shortDate(event.schedule_end_date)"
      />
      <EventRegistrationsComponent
        v-if="event.registration_required || event.number_of_registrations"
        class="mb-1"
        :numberOfRegistrations="event.number_of_registrations"
        :registrationLimit="event.registration_limit"
      />
      <EventWaitlistRegistrationsComponent
        v-if="
          event.registration_required && event.number_of_waitlist_registrations
        "
        class="mb-1"
        :numberOfWaitlistRegistrations="event.number_of_waitlist_registrations"
      />
      <EventFreeTagComponent v-if="event.is_free_event" class="mt-3 mb-1" />
      <EventStudentComponent v-if="event.student" :student="event.student" />
      <div
        class="row"
        v-if="event.event_description || event.event_description_new"
      >
        <div class="col-md-12">
          <hr />
          <EventCollapsableDescriptionComponent
            :descriptionHtml="event.event_description"
            :descriptionJson="event.event_description_new"
          />
        </div>
      </div>
      <div
        v-show="!event.is_cancelled"
        v-if="
          streamOptions.zoom ||
          streamOptions.browser ||
          streamOptions.googleMeet
        "
      >
        <hr />
        <div class="container mb-2">
          <EventZoomStreamingComponent
            v-if="streamOptions.zoom"
            class="row"
            :event="event"
          />
          <hr class="row my-3" />
          <EventGoogleMeetStreamingComponent
            v-if="streamOptions.googleMeet"
            class="row"
            :event="event"
          />
          <hr class="row my-3" />
          <EventBrowserStreamingComponent
            v-if="streamOptions.browser"
            class="row"
            :event="event"
          />
        </div>
      </div>
      <hr class="mt-4 mb-4" />
    </div>
    <div class="container p-0">
      <div class="row mt-4">
        <div class="col-12">
          <div
            class="d-flex flex-column flex-sm-row justify-content-sm-between"
          >
            <div
              class="order-1 order-sm-0"
              v-tooltip="{
                content:
                  event.number_of_registrations > 0
                    ? eventRemoveButtonTooltip
                    : '',
                delay: 100,
                placement: 'top',
                container: 'body',
              }"
            >
              <HMButton
                color="outline-gray"
                class="w-100 mt-2 mt-sm-0"
                :loading="removingEvent"
                @click="deleteEvent()"
                :disabled="event.number_of_registrations > 0"
              >
                <i class="far fa-trash-alt pr-2 c-medium-gray"></i
                >{{ $t('EVENTS.BUTTON.REMOVE') }}
              </HMButton>
            </div>
            <div class="d-flex flex-column flex-sm-row order-0 order-sm-1">
              <HMButton
                v-if="!isMeetingEvent"
                color="outline-gray mr-sm-2 mt-2 mt-sm-0 order-1 order-sm-0"
                @click="duplicate()"
              >
                <i class="far fa-clone pr-2 c-medium-gray"></i
                >{{ $t('EVENTS.BUTTON.DUPLICATE') }}
              </HMButton>
              <HMButton
                v-if="!isMeetingEvent"
                color="outline-gray mt-2 mt-sm-0 order-0 order-sm-1"
                @click="manageEvent()"
              >
                <i class="far fa-pencil-alt pr-2 c-coral"></i
                >{{ $t('EVENTS.BUTTON.MANAGE') }}
              </HMButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HMModalHeader from '@/web/components/common/HMModalHeader';
import * as EventUtils from '@/core/utils/events';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import EventCollapsableDescriptionComponent from '@/web/components/events/EventCollapsableDescriptionComponent';
import EventDateTimeComponent from '@/web/components/events/EventDateTimeComponent';
import EventNameComponent from '@/web/components/events/EventNameComponent';
import EventTypeComponent from '@/web/components/events/EventTypeComponent';
import EventDurationComponent from '@/web/components/events/EventDurationComponent';
import EventLocationComponent from '@/web/components/events/EventLocationComponent';
import EventRepeatsComponent from '@/web/components/events/EventRepeatsComponent';
import EventRegistrationsComponent from '@/web/components/events/EventRegistrationsComponent';
import EventWaitlistRegistrationsComponent from '@/web/components/events/EventWaitlistRegistrationsComponent';
import EventFreeTagComponent from '@/web/components/events/EventFreeTagComponent';
import EventStudentComponent from '@/web/components/events/EventStudentComponent';
import EventZoomStreamingComponent from '@/web/components/events/EventZoomStreamingComponent';
import EventGoogleMeetStreamingComponent from '@/web/components/events/EventGoogleMeetStreamingComponent';
import EventBrowserStreamingComponent from '@/web/components/events/EventBrowserStreamingComponent';
import EventCancelledComponent from '@/web/components/events/EventCancelledComponent';
import HMDialog from '@/web/components/common/HMDialog';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';
import HMSpinner from '@/web/components/common/HMSpinner';
import { EVENT_TYPE_MEETING } from '../../../../core/utils/events';

export default {
  name: 'EventStartStopModal',
  components: {
    HMSpinner,
    HMModalHeader,
    EventNameComponent,
    EventTypeComponent,
    EventCancelledComponent,
    EventCollapsableDescriptionComponent,
    EventDateTimeComponent,
    EventDurationComponent,
    EventLocationComponent,
    EventRepeatsComponent,
    EventRegistrationsComponent,
    EventWaitlistRegistrationsComponent,
    EventZoomStreamingComponent,
    EventGoogleMeetStreamingComponent,
    EventBrowserStreamingComponent,
    EventStudentComponent,
    EventFreeTagComponent,
  },
  mixins: [TeacherPermissionsMixin],
  props: {
    refreshCalendarFn: null,
  },
  data() {
    return {
      removingEvent: false,
    };
  },
  computed: {
    ...mapGetters({
      event: 'event/event',
      isZoomConnected: 'user/isZoomConnected',
    }),
    isMeetingEvent() {
      // meetings are 1:1 scheduled events and do not appear on the public calendar
      return this.event.event_type === EVENT_TYPE_MEETING;
    },
    eventTypeLabel() {
      const event = this.event;
      const key = EventUtils.getEventTypeLabelKey(this.event.event_type);
      return this.$t(key);
    },
    eventTypeClasses() {
      return EventUtils.getEventTypeIconClasses(this.event.event_type);
    },
    eventRepeatsDayOfWeek() {
      return EventUtils.getDayName(this.event.schedule_day_of_week);
    },
    streamOptions() {
      const options =
        EventUtils.getEventStreamOptions(this.event.event_type) || {};
      if (!this.hasPermission('has_tokbox_streaming')) {
        return { ...options, browser: false };
      }
      return options;
    },
    eventRemoveButtonTooltip() {
      if (this.event.number_of_registrations === 0) return '';

      return this.$t('EVENTS.TEXT.CAN_NOT_REMOVE_EVENT_WITH_REGISTRATIONS');
    },
  },
  methods: {
    shortDate(date) {
      return EventUtils.shortDate(date);
    },
    shortTime(datetime) {
      return EventUtils.shortTime(datetime);
    },
    humanizeDuration(duration) {
      return EventUtils.humanizeDuration(duration, 'minutes');
    },
    duplicate() {
      this.$emit('close'); // close the modal
      this.$router.push({
        name: 'events-duplicate',
        params: { id: this.event.id },
      });
    },
    manageEvent() {
      this.$emit('close'); // close the modal
      this.$router.push({
        name: 'events-details',
        params: { id: this.event.id },
      });
    },
    async deleteEvent() {
      if (this.event.number_of_registrations > 0) {
        const title = this.$t('GENERAL.TITLE.INFORMATION');
        const subtitle = this.$t('GENERAL.TEXT.ACTION_NOT_PERMITTED');
        const text = this.$t(
          'EVENTS.TEXT.CAN_NOT_REMOVE_EVENT_WITH_REGISTRATIONS'
        );
        const confirmButton = null;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        const confirm = () => {};

        this.$modal.show(HMDialog, {
          title,
          subtitle,
          text,
          confirmButton,
          confirm,
        });
        return;
      }

      const title = this.$t('EVENTS.TITLE.DELETE_EVENT');
      const text = this.$t('EVENTS.TEXT.DELETE_EVENT');
      const confirmButton = this.$t('EVENTS.BUTTON.DELETE_EVENT');
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const confirm = async () => {
        try {
          this.removingEvent = true;
          await this.$store.dispatch('event/deleteEvent', this.event.id);
          this.$notify({
            group: 'app',
            type: 'success',
            title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
            text: this.$t('NOTIFICATIONS.TEXT.EVENT_SUCCESSFULLY_REMOVED'),
          });
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to remove the event',
            text: errorToMessage(error.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        } finally {
          this.removingEvent = false;
        }
        if (this.refreshCalendarFn) this.refreshCalendarFn();
        this.$emit('close');
      };

      this.$modal.show(HMDialog, {
        title,
        text,
        confirmButton,
        confirm,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
</style>
